import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Building2, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@mascot.com");
  const [password, setPassword] = useState("Admin@123");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/");
    } catch (err) {
      toast.error("Login failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-zinc-100">
      {/* Left: form */}
      <div className="w-full lg:w-[520px] flex flex-col p-10 lg:p-14 bg-white border-r border-zinc-200">
        <div className="flex items-center gap-2 mb-16">
          <Building2 className="text-orange-600 h-7 w-7" strokeWidth={2.4} />
          <div>
            <div className="font-heading font-black text-lg tracking-tight">MASCOT</div>
            <div className="overline">Construction • Second Brain</div>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm">
          <div className="overline mb-3">Secure Access</div>
          <h1 className="font-heading font-black text-4xl leading-tight tracking-tight mb-3">
            Sign in to your <span className="text-orange-600">command room</span>
          </h1>
          <p className="text-sm text-zinc-500 mb-8">
            Manage NHAI & PWD projects, DPRs, BOQs, vendor agreements, royalty & legal
            documents — all in one place.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest font-medium text-zinc-600">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-sm mt-1.5 h-11 focus-visible:ring-orange-500 border-zinc-300"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-medium text-zinc-600">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm mt-1.5 h-11 focus-visible:ring-orange-500 border-zinc-300"
              />
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 rounded-sm h-11 mt-2 font-medium"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>Enter Dashboard <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200 text-xs text-zinc-500">
            Don&apos;t have an account?{" "}
            <Link
              data-testid="login-register-link"
              to="/register"
              className="text-orange-600 font-medium hover:underline"
            >
              Register a new user
            </Link>
          </div>

          <div className="mt-6 p-3 border border-zinc-200 bg-zinc-50 rounded-sm font-mono text-[11px] text-zinc-600">
            <div className="overline text-[9px] mb-1">Demo credentials</div>
            admin@mascot.com / Admin@123
          </div>
        </div>

        <div className="text-[11px] text-zinc-400 font-mono mt-10">
          © 2026 Mascot Construction Co. — Internal system
        </div>
      </div>

      {/* Right: hero image */}
      <div
        className="hidden lg:block flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1536099629323-44806c1ea264?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxoaWdod2F5JTIwcm9hZCUyMGNvbnN0cnVjdGlvbiUyMGRyb25lJTIwdmlld3xlbnwwfHx8fDE3ODM0MjU4MjR8MA&ixlib=rb-4.1.0&q=85)",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/70 via-[#0A0A0A]/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="overline text-orange-400 mb-3">Infrastructure • Precision • Trust</div>
          <div className="font-heading font-black text-5xl leading-[1.05] max-w-lg tracking-tight">
            Every kilometre. Every drawing. Every rupee — accountable.
          </div>
          <div className="mt-4 font-mono text-xs text-zinc-300 max-w-md">
            AI-powered document intelligence for road construction operations.
          </div>
        </div>
      </div>
    </div>
  );
}
